import { useEffect, useState, useCallback } from "react";
import { FaSave, FaSpinner, FaUndo } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./RolePermissions.css";

const API_URL = process.env.REACT_APP_API_URL;

const ACTIONS = ["view", "add", "edit", "delete"];
const ROLES = ["admin", "user"];
const ROLE_LABELS = { admin: "Admin", user: "User" };


function RolePermissions() {

    // matrix[role][menu_key] = { view, add, edit, delete }
    const [matrix, setMatrix] = useState({});

    const [menus, setMenus] = useState([]);

    const [isLoading, setIsLoading] = useState(true);

    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(async () => {

        setIsLoading(true);

        try {

            const [menusRes, rolesRes] = await Promise.all([
                fetch(`${API_URL}/portal/permissions/menus`),
                fetch(`${API_URL}/portal/permissions/roles`)
            ]);

            const menusData = await menusRes.json();

            const rolesData = await rolesRes.json();

            setMenus(menusData || []);

            const nextMatrix = {};

            ROLES.forEach(role => { nextMatrix[role] = {}; });

            (rolesData.data || []).forEach(row => {

                nextMatrix[row.role][row.menu_key] = {
                    view: row.can_view,
                    add: row.can_add,
                    edit: row.can_edit,
                    delete: row.can_delete
                };

            });

            setMatrix(nextMatrix);

        } catch (err) {

            console.error("Failed to load permission matrix:", err);

            toast.error("Unable to load the permission matrix.");

        } finally {

            setIsLoading(false);

        }

    }, []);

    useEffect(() => {

        load();

    }, [load]);

    const toggleCell = (role, menuKey, action) => {

        setMatrix(prev => {

            const current = prev[role]?.[menuKey] || { view: false, add: false, edit: false, delete: false };

            return {

                ...prev,

                [role]: {

                    ...prev[role],

                    [menuKey]: {

                        ...current,

                        [action]: !current[action]

                    }

                }

            };

        });

    };

    const setFullRow = (role, menuKey, value) => {

        setMatrix(prev => ({

            ...prev,

            [role]: {

                ...prev[role],

                [menuKey]: { view: value, add: value, edit: value, delete: value }

            }

        }));

    };

    const handleSave = async () => {

        setIsSaving(true);

        try {

            const rows = [];

            ROLES.forEach(role => {

                menus.forEach(menu => {

                    const cell = matrix[role]?.[menu.key] || { view: false, add: false, edit: false, delete: false };

                    rows.push({

                        role,

                        menu_key: menu.key,

                        can_view: cell.view,

                        can_add: cell.add,

                        can_edit: cell.edit,

                        can_delete: cell.delete

                    });

                });

            });

            const res = await fetch(`${API_URL}/portal/permissions/roles`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({ rows })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Permission matrix saved. Takes effect for everyone on their next page load.");

            } else {

                toast.error(data.message || data.detail || "Failed to save the permission matrix.");

            }

        } catch (err) {

            console.error("Failed to save permission matrix:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsSaving(false);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Role Permissions</h2>
                    <p className="page-subtitle">
                        Controls which menus each role can see, and whether they can view / add / edit / delete
                        within them.
                    </p>
                 {/*     SuperAdmin always has full access and isn't shown here.
                        Individual users can also be given a personal override from Manage Admins / Manage Users. */}
                </div>

                <button
                    type="button"
                    className="add-btn"
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Changes</>}
                </button>

            </div>

            {

                isLoading ? (

                    <p className="empty-note"><FaSpinner className="spin" /> Loading matrix...</p>

                ) : (

                    ROLES.map(role => (

                        <div className="matrix-block" key={role}>

                            <h3 className="matrix-role-title">{ROLE_LABELS[role]}</h3>

                            <div className="matrix-table-wrapper">

                                <table className="matrix-table">

                                    <thead>

                                        <tr>
                                            <th className="menu-col">Menu</th>
                                            {ACTIONS.map(a => <th key={a}>{a[0].toUpperCase() + a.slice(1)}</th>)}
                                            <th>Quick set</th>
                                        </tr>

                                    </thead>

                                    <tbody>

                                        {menus.map(menu => {

                                            const cell = matrix[role]?.[menu.key] || { view: false, add: false, edit: false, delete: false };

                                            return (

                                                <tr key={menu.key}>

                                                    <td className="menu-col">{menu.label}</td>

                                                    {ACTIONS.map(action => (

                                                        <td key={action} className="checkbox-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!cell[action]}
                                                                onChange={() => toggleCell(role, menu.key, action)}
                                                            />
                                                        </td>

                                                    ))}

                                                    <td className="quick-set-col">

                                                        <button
                                                            type="button"
                                                            className="quick-set-btn"
                                                            onClick={() => setFullRow(role, menu.key, true)}
                                                        >
                                                            All
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="quick-set-btn"
                                                            onClick={() => setFullRow(role, menu.key, false)}
                                                        >
                                                            <FaUndo /> None
                                                        </button>

                                                    </td>

                                                </tr>

                                            );

                                        })}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    ))

                )

            }

        </div>

    );

}

export default RolePermissions;
